"use client";
import type { AtlasFile } from "@-/prisma";
import { getManagedUploadPolicy } from "./upload-policy";
import type { ManagedUploadPolicy } from "./upload-policy";

const CLIENT_UPLOAD_REGISTER_EVENT = "atlas-file.register-client-upload";
const DEFAULT_CLIENT_UPLOAD_MAX_BYTES = 1024 * 1024 * 1024;
const CLIENT_UPLOAD_MULTIPART_THRESHOLD_BYTES = 8 * 1024 * 1024;

type UploadFileResponse = {
  success: boolean;
  file: AtlasFile;
};

type UploadMode = "server" | "client";

type UploadFileInput = {
  agencyMembershipId?: string;
  applicationId?: string;
  file: File;
  organizationMembershipId?: string;
  placementId?: string;
  schoolId?: string;
  projectId?: string;
  resourceId?: string;
  userId?: string;
  uploadMode?: UploadMode;
  maximumSizeInBytes?: number;
  allowedContentTypes?: string[];
  uploadPolicy?: ManagedUploadPolicy;
  purpose?: "folder";
};

type ClientUploadPayload = {
  agencyMembershipId?: string;
  applicationId?: string;
  fileName: string;
  uploadId: string;
  contentType: string;
  fileSize: number;
  maximumSizeInBytes: number;
  allowedContentTypes?: string[];
  uploadPolicy?: ManagedUploadPolicy;
  purpose?: "folder";
  organizationMembershipId?: string;
  placementId?: string;
  schoolId?: string;
  projectId?: string;
  userId?: string;
};

export class UploadAssociationError extends Error {
  constructor(
    message: string,
    readonly retainedUpload: {
      payload: object;
      blob: object;
    },
    readonly nativeAssociation: "REFUSED" | "UNCONFIRMED",
  ) {
    super(message);
    this.name = "UploadAssociationError";
  }
}

function normalizeAllowedContentTypes(allowedContentTypes?: string[]) {
  if (!allowedContentTypes?.length || allowedContentTypes.includes("*")) {
    return undefined;
  }

  return allowedContentTypes;
}

function getManagedUploadMode(): UploadMode {
  return process.env.NEXT_PUBLIC_ATLAS_E2E_UPLOAD_MODE === "server" ? "server" : "client";
}

function formatUploadSize(bytes: number) {
  return `${Math.round(bytes / (1024 * 1024))}MB`;
}

function sanitizeUploadFileName(fileName: string) {
  const sanitized = fileName
    .replace(/[\\/]/g, "-")
    .replace(/[#?]/g, "-")
    .trim();

  return sanitized.length > 0 ? sanitized : "file";
}

function sanitizeUploadPathSegment(segment: string) {
  return segment
    .replace(/[\\/]/g, "-")
    .replace(/[#?]/g, "-")
    .trim();
}

function buildManagedUploadPath({
  fileName,
  uploadId,
  organizationMembershipId,
  schoolId,
  projectId,
  userId,
}: ClientUploadPayload) {
  const scopedPrefix = schoolId
    ?? organizationMembershipId
    ?? projectId
    ?? userId;
  const sanitizedFileName = sanitizeUploadFileName(fileName);
  const uploadFileName = `${sanitizeUploadPathSegment(uploadId)}-${sanitizedFileName}`;

  if (!scopedPrefix) {
    return uploadFileName;
  }

  return `${sanitizeUploadPathSegment(scopedPrefix)}/${uploadFileName}`;
}

function isContentTypeAllowed(
  contentType: string,
  allowedContentTypes?: string[],
) {
  if (!allowedContentTypes?.length || allowedContentTypes.includes("*")) {
    return true;
  }

  return allowedContentTypes.some((allowedContentType) => {
    if (allowedContentType === contentType) {
      return true;
    }

    if (!allowedContentType.endsWith("/*")) {
      return false;
    }

    const prefix = allowedContentType.slice(0, -1);
    return contentType.startsWith(prefix);
  });
}

async function readUploadFileResponse(response: Response) {
  const text = await response.text();
  let data: Partial<UploadFileResponse> & { error?: string; message?: string; retainedUpload?: { payload?: unknown; blob?: unknown } } = {};

  if (text) {
    try {
      data = JSON.parse(text) as typeof data;
    } catch {
      if (!response.ok) {
        throw new Error(`Upload failed with status ${response.status}: ${text || response.statusText}`);
      }

      throw new Error("Upload response was not valid JSON");
    }
  }

  if (!response.ok) {
    const message = data.message || data.error || `Upload failed with status ${response.status}`;
    const retained = data.retainedUpload;
    if (
      retained?.payload && typeof retained.payload === "object" && !Array.isArray(retained.payload)
      && retained.blob && typeof retained.blob === "object" && !Array.isArray(retained.blob)
    ) {
      throw new UploadAssociationError(message, { payload: retained.payload, blob: retained.blob }, response.status === 409 ? "REFUSED" : "UNCONFIRMED");
    }
    throw new Error(message);
  }

  if (!data.file) {
    throw new Error("Upload response did not include a file");
  }

  return data.file;
}

async function uploadFileWithClientUpload({
  agencyMembershipId,
  applicationId,
  file,
  organizationMembershipId,
  placementId,
  schoolId,
  projectId,
  userId,
  maximumSizeInBytes = DEFAULT_CLIENT_UPLOAD_MAX_BYTES,
  allowedContentTypes,
  uploadPolicy,
  purpose,
}: UploadFileInput) {
  const { upload } = await import("@vercel/blob/client");
  const uploadId = crypto.randomUUID();
  const contentType = file.type || "application/octet-stream";
  const policy = getManagedUploadPolicy(uploadPolicy);
  const maxUploadSize = Math.min(policy?.maximumSizeInBytes ?? maximumSizeInBytes, DEFAULT_CLIENT_UPLOAD_MAX_BYTES);
  const normalizedAllowedContentTypes = normalizeAllowedContentTypes(policy?.allowedContentTypes ?? allowedContentTypes);

  if (file.size > maxUploadSize) {
    throw new Error(`File size must be less than ${formatUploadSize(maxUploadSize)}`);
  }

  if (!isContentTypeAllowed(contentType, normalizedAllowedContentTypes)) {
    throw new Error("File type is not allowed");
  }

  const payload: ClientUploadPayload = {
    fileName: file.name,
    uploadId,
    contentType,
    fileSize: file.size,
    maximumSizeInBytes: maxUploadSize,
    allowedContentTypes: normalizedAllowedContentTypes,
    uploadPolicy,
    purpose,
    agencyMembershipId,
    applicationId,
    organizationMembershipId,
    placementId,
    schoolId,
    projectId,
    userId,
  };
  const pathname = buildManagedUploadPath(payload);
  const blob = await upload(pathname, file, {
    access: "public",
    contentType,
    handleUploadUrl: "/api/files/upload",
    clientPayload: JSON.stringify(payload),
    multipart: file.size > CLIENT_UPLOAD_MULTIPART_THRESHOLD_BYTES,
  });

  let response: Response | undefined;
  try {
    response = await fetch("/api/files/upload", {
      method: "POST",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify({
        type: CLIENT_UPLOAD_REGISTER_EVENT,
        payload: {
          ...payload,
          blob,
        },
      }),
    });
    return await readUploadFileResponse(response);
  } catch (error) {
    throw new UploadAssociationError(error instanceof Error ? error.message : "Upload association was not confirmed.", { payload, blob }, response?.status === 409 ? "REFUSED" : "UNCONFIRMED");
  }
}

export async function uploadFile({
  agencyMembershipId,
  applicationId,
  file,
  organizationMembershipId,
  placementId,
  schoolId,
  projectId,
  userId,
  uploadMode = "server",
  maximumSizeInBytes,
  allowedContentTypes,
  uploadPolicy,
  purpose,
}: UploadFileInput): Promise<AtlasFile> {
  if (uploadMode === "client") {
    return uploadFileWithClientUpload({
      agencyMembershipId,
      applicationId,
      file,
      organizationMembershipId,
      placementId,
      schoolId,
      projectId,
      userId,
      maximumSizeInBytes,
      allowedContentTypes,
      uploadPolicy,
      purpose,
    });
  }

  const formData = new FormData();
  formData.append("file", file);

  if (organizationMembershipId) {
    formData.append("organizationMembershipId", organizationMembershipId);
  }

  if (agencyMembershipId) {
    formData.append("agencyMembershipId", agencyMembershipId);
  }

  if (applicationId) {
    formData.append("applicationId", applicationId);
  }

  if (placementId) {
    formData.append("placementId", placementId);
  }

  if (userId) {
    formData.append("userId", userId);
  }

  if (schoolId) {
    formData.append("schoolId", schoolId);
  }

  if (projectId) {
    formData.append("projectId", projectId);
  }

  if (purpose) formData.append("purpose", purpose);

  if (uploadPolicy) {
    formData.append("uploadPolicy", uploadPolicy);
  }

  const response = await fetch("/api/files/upload", {
    method: "POST",
    body: formData,
  });

  return readUploadFileResponse(response);
}

export async function uploadManagedFile(
  input: Omit<UploadFileInput, "uploadMode">,
): Promise<AtlasFile> {
  return uploadFile({
    ...input,
    uploadMode: getManagedUploadMode(),
  });
}
